// Переписка с соискателями, помощник и библиотека утверждённых ответов.
// Формы — зеркало contracts/openapi/bark-hiring.v1.yaml.
//
// **Часть полей здесь названа по-русски, и это не описка.** Сервер отдаёт `на_паузе`,
// `ждут_подтверждения`, `сообщения`, `текст` — так объявлены сериализаторы найма
// (`server/app/hiring/serializers/dialogs.py`). Переименовать их на клиенте нельзя: имя поля
// в ответе — это контракт, а «удобное» имя рядом с ним означало бы два словаря на одно поле и
// молчаливо пустой экран после первой же правки на сервере.
//
// Что важно не потерять по дороге к экрану:
//
// * **автор виден у каждого сообщения** — прямое требование сценария 101. Приходит парой
//   `author_kind` и `author_title`, плюс имя сотрудника, если писал человек;
// * **пауза и выключенный помощник — разные факты** (правило 7). `на_паузе` означает, что в
//   диалог написал человек и помощник молчит до явного возврата; выключенный режим живёт в
//   настройках учётной записи и к диалогу отношения не имеет;
// * **пустой `текст`** означает, что текст стёрт по требованию человека или по сроку, а не
//   что сообщение было пустым.

import { request } from '@/shared/api/client';
import { queryString } from '@/shared/api/query';
import type { ListResponse } from '@/shared/api/types';

const BASE = '/api/v1/hiring';

/** contracts/enums/statuses.yaml, hiring_dialog_status. Шесть значений тз 5.8.1, дословно. */
export const DIALOG_STATUSES = [
  'awaiting_first_reply',
  'assistant',
  'waiting_operator',
  'operator',
  'paused',
  'finished',
] as const;

export type DialogStatus = (typeof DIALOG_STATUSES)[number];

/** contracts/enums/statuses.yaml, hiring_assistant_mode. */
export const ASSISTANT_MODES = ['off', 'suggest', 'auto'] as const;
export type AssistantMode = (typeof ASSISTANT_MODES)[number];

/** Чем помощник говорит: готовыми формулировками или своими словами. */
export const ASSISTANT_SPEECH_MODES = ['library', 'free'] as const;
export type AssistantSpeechMode = (typeof ASSISTANT_SPEECH_MODES)[number];

export type MessageAuthor = 'applicant' | 'assistant' | 'operator' | 'platform';

export type MessageState =
  'proposed' | 'approved' | 'sent' | 'failed' | 'rejected_by_guard' | 'discarded';

// ── Список переписок ──────────────────────────────────────────────────────────────────────

/** Диалог в списке переписок. */
export interface DialogRow {
  id: string;
  account_id: string;
  account_title: string;
  candidate_id: string | null;
  candidate_name: string | null;
  external_chat_id: string;
  status: DialogStatus;
  /** Название состояния по-русски, из контракта. Своего словаря заводить не нужно. */
  status_title: string;
  current_step_code: string | null;
  last_author_kind: string | null;
  last_message_at: string | null;
  /** Помощник молчит: диалог забрал человек. */
  на_паузе: boolean;
  /** Сколько предложенных ответов ждут решения. */
  ждут_подтверждения: number;
  /** Сколько передач человеку ещё не закрыто. */
  открытых_передач: number;
}

/** Одно сообщение переписки. Автор виден всегда — это требование сценария 101. */
export interface DialogMessage {
  id: string;
  seq: number;
  author_kind: MessageAuthor;
  /** Кто написал: соискатель, помощник, сотрудник, площадка. */
  author_title: string;
  author_user_id: string | null;
  /** Имя сотрудника, если писал человек. */
  author_name: string | null;
  state: MessageState;
  state_title: string;
  step_code: string | null;
  library_entry_id: string | null;
  /** Участвовала ли модель в разборе предыдущего сообщения. */
  моделью: boolean;
  /** Собственный робот площадки: помощнику такое не передаётся. */
  служебное_площадки: boolean;
  approved_by_user_id: string | null;
  sent_at: string | null;
  created_at: string;
  /** Пусто — текст стёрт по требованию человека или по сроку. */
  текст: string | null;
  /** Что проверил сторож исходящего и с каким итогом. */
  сторож?: Record<string, unknown> | null;
}

/** Проставленное поле квалификации: значение и кем проставлено. */
export interface DialogField {
  id: string;
  field_code: string;
  value_text: string | null;
  source: string;
  source_title: string;
  confidence: string | null;
  set_by_user_id: string | null;
  set_by_name: string | null;
  message_id: string | null;
  created_at: string;
}

/** Передача диалога человеку: причина из справочника. */
export interface DialogHandoff {
  id: string;
  reason_code: string;
  reason_title: string;
  detail: string | null;
  notified_at: string | null;
  resolved_at: string | null;
  created_at: string;
}

/** Полный журнал одного диалога: сообщения, поля, передачи. */
export interface DialogLog {
  диалог: DialogRow;
  сообщения: DialogMessage[];
  поля: DialogField[];
  передачи: DialogHandoff[];
}

/**
 * Что стало с диалогом после действия.
 *
 * `исход` — одно из: `служебное`, `повтор`, `пауза`, `ответил`, `предложил`, `передал`,
 * `молчит` (`server/app/hiring/services/dialog.py`). `примечание` — та же мысль словами, её
 * и показываем человеку: пересказывать код своими словами значит завести второй словарь.
 */
export interface ActionResult {
  dialog_id: string;
  исход: string;
  примечание?: string;
  step_code?: string | null;
  handoff_reason?: string | null;
  message_id?: string | null;
}

export function listDialogs(
  params: { status?: string; account_id?: string; limit?: number; offset?: number },
  signal?: AbortSignal,
): Promise<ListResponse<DialogRow>> {
  return request(`${BASE}/dialogs${queryString({ ...params })}`, { signal });
}

export function fetchDialog(dialogId: string, signal?: AbortSignal): Promise<DialogLog> {
  return request(`${BASE}/dialogs/${dialogId}`, { signal });
}

/** Написать соискателю самому: помощник встаёт на паузу по этому диалогу. */
export function sendOperatorMessage(dialogId: string, текст: string): Promise<ActionResult> {
  return request(`${BASE}/dialogs/${dialogId}/messages`, { method: 'POST', body: { текст } });
}

/** Вернуть помощника в диалог. Только это снимает паузу. */
export function resumeAssistant(dialogId: string): Promise<ActionResult> {
  return request(`${BASE}/dialogs/${dialogId}/resume`, { method: 'POST' });
}

export function approveMessage(messageId: string): Promise<ActionResult> {
  return request(`${BASE}/dialogs/messages/${messageId}/approve`, { method: 'POST' });
}

export function discardMessage(messageId: string): Promise<ActionResult> {
  return request(`${BASE}/dialogs/messages/${messageId}/discard`, { method: 'POST' });
}

// ── Режим помощника по учётной записи ─────────────────────────────────────────────────────

/** Режим помощника по ОДНОЙ учётной записи площадки: переключатель уровня записи, не диалога. */
export interface AssistantSettings {
  account_id: string;
  account_title: string;
  mode: AssistantMode;
  mode_title: string;
  /** Чем говорит: `library` — утверждённые фразы, `free` — своими словами под сторожем. */
  speech_mode: AssistantSpeechMode;
  speech_mode_title: string;
  /** Вилка оплаты в копейках: число вне неё сторож исходящего не пропустит. */
  pay_min_kopecks: number | null;
  pay_max_kopecks: number | null;
  /** Ниже этого порога помощник не отвечает сам, а передаёт человеку. */
  confidence_threshold: number;
  /** Замолчать после сообщения человека до явного возврата. */
  silence_after_human: boolean;
  changed_at: string | null;
  changed_by_user_id: string | null;
}

export interface AssistantSettingsIn {
  mode?: AssistantMode | null;
  speech_mode?: AssistantSpeechMode | null;
  pay_min_kopecks?: number | null;
  pay_max_kopecks?: number | null;
  confidence_threshold?: number | null;
  silence_after_human?: boolean | null;
}

/** Шаг разговора: что спрашиваем и какое поле этим заполняем. */
export interface ScriptStep {
  code: string;
  order_no: number;
  title: string;
  slot_code: string | null;
  is_required: boolean;
  skip_if_known: boolean;
  is_active: boolean;
}

/** Строка стоп-листа: чего помощнику говорить нельзя. */
export interface Prohibition {
  code: string;
  title: string;
  kind: string;
  pattern: string;
}

export function listAssistantSettings(
  signal?: AbortSignal,
): Promise<ListResponse<AssistantSettings>> {
  return request(`${BASE}/assistant/settings`, { signal });
}

export function saveAssistantSettings(
  accountId: string,
  body: AssistantSettingsIn,
): Promise<AssistantSettings> {
  return request(`${BASE}/assistant/settings/${accountId}`, { method: 'PUT', body });
}

export function listScript(
  accountId: string | undefined,
  signal?: AbortSignal,
): Promise<ListResponse<ScriptStep>> {
  return request(`${BASE}/assistant/script${queryString({ account_id: accountId })}`, { signal });
}

export function listProhibitions(signal?: AbortSignal): Promise<ListResponse<Prohibition>> {
  return request(`${BASE}/assistant/prohibitions`, { signal });
}

// ── Библиотека утверждённых ответов ───────────────────────────────────────────────────────

export type LibraryStatus = 'draft' | 'approved' | 'retired';

/** Формулировка библиотеки: текст, состояние, кто завёл и кто утвердил. */
export interface LibraryEntry {
  id: string;
  account_id: string | null;
  step_code: string;
  kind: string;
  text: string;
  status: LibraryStatus;
  status_title: string;
  source: string;
  usage_count: number;
  author_user_id: string | null;
  author_name: string | null;
  approved_by_user_id: string | null;
  approved_by_name: string | null;
  approved_at: string | null;
  created_at: string;
}

export interface LibraryDraftIn {
  step_code: string;
  text: string;
  /** question, answer, closing или fallback. */
  kind?: string;
  account_id?: string | null;
}

export function listLibrary(
  params: { step_code?: string; status?: string; account_id?: string; limit?: number },
  signal?: AbortSignal,
): Promise<ListResponse<LibraryEntry>> {
  return request(`${BASE}/library${queryString({ ...params })}`, { signal });
}

export function createLibraryDraft(body: LibraryDraftIn): Promise<LibraryEntry> {
  return request(`${BASE}/library`, { method: 'POST', body });
}

/**
 * Поправить текст формулировки. Состояние остаётся прежним.
 *
 * Заведено 14.09.2026: заказчик не нашёл кнопки правки, и её не было ни на экране, ни на
 * сервере. Правка утверждённой фразы НЕ сбрасывает утверждение: на шаге разговора утверждённая
 * фраза обычно одна, и сброс оставил бы помощника без слов посреди разговора с соискателем.
 */
export function editLibraryEntry(entryId: string, text: string): Promise<LibraryEntry> {
  return request(`${BASE}/library/${entryId}`, { method: 'PATCH', body: { text } });
}

/** Убрать формулировку насовсем. Отправленную сервер убрать не даст: её выводят из обращения. */
export function deleteLibraryEntry(entryId: string): Promise<void> {
  return request(`${BASE}/library/${entryId}`, { method: 'DELETE' });
}

export function approveLibraryEntry(entryId: string): Promise<LibraryEntry> {
  return request(`${BASE}/library/${entryId}/approve`, { method: 'POST' });
}

export function retireLibraryEntry(entryId: string): Promise<LibraryEntry> {
  return request(`${BASE}/library/${entryId}/retire`, { method: 'POST' });
}

// ── Выгрузки прошлых переписок заказчика ──────────────────────────────────────────────────

/** Разбор выгрузки: сколько нашли, сколько предложили, сколько утвердили. */
export interface TranscriptImport {
  id: string;
  source_kind: string;
  status: string;
  messages_total: number;
  pairs_total: number;
  proposed_total: number;
  approved_total: number;
  protocol: string | null;
  created_at: string;
}

/** Пара из выгрузки, ждущая решения человека. Тексты обезличены ДО записи. */
export interface TranscriptPair {
  id: string;
  import_id: string;
  applicant_text_masked: string;
  recruiter_text_masked: string;
  step_code_suggested: string | null;
  score: string | null;
  verdict: string;
  model_note: string | null;
  reject_reason: string | null;
  decided_by_user_id: string | null;
  decided_at: string | null;
}

export function listTranscripts(signal?: AbortSignal): Promise<ListResponse<TranscriptImport>> {
  return request(`${BASE}/library/transcripts`, { signal });
}

export function listTranscriptPairs(
  importId: string,
  signal?: AbortSignal,
): Promise<ListResponse<TranscriptPair>> {
  return request(`${BASE}/library/transcripts/${importId}/pairs`, { signal });
}

export function analyzeTranscript(importId: string): Promise<TranscriptImport> {
  return request(`${BASE}/library/transcripts/${importId}/analyze`, { method: 'POST' });
}

export function decideTranscriptPair(
  pairId: string,
  body: {
    verdict: 'approved' | 'rejected';
    step_code?: string | null;
    reject_reason?: string | null;
  },
): Promise<TranscriptPair> {
  return request(`${BASE}/library/transcripts/pairs/${pairId}/decide`, { method: 'POST', body });
}
